import * as React from 'react';
import { Button } from '@/hen-ui/atomics/Button';
import { Icon } from '@/hen-ui/atomics/Icon';
import { Text } from '@/hen-ui/atomics/Text';
import { RadioItemLabelContext } from '@/hen-ui/atomics/Radio/radio-label-context';
import { COMPONENT_IDS } from '@/hen-ui/catalog/component-ids';
import { Flex } from '@/hen-ui/composites/Flex';
import { Tooltip, type TooltipProps } from '@/hen-ui/composites/Tooltip';
import { cn } from '@/hen-ui/utils';

import {
  ratingStarFillVariants,
  ratingStarStrokeVariants,
  type RatingStarTone,
} from './rating-star.variants';
import { getStarFills } from './RatingStar';

export interface PartnerStarRatingProps {
  rating: number;
  ratingCount?: string;
  /**
   * When true and {@link ratingCount} is set, renders the count as visible text.
   * Defaults to false (count may still appear in the aria-label).
   */
  showRatingCount?: boolean;
  /**
   * Appended after {@link ratingCount} in the group aria-label when a count is
   * present (e.g. `' reviews'` for banner surfaces that store a bare count).
   */
  ratingCountAriaSuffix?: string;
  /** Typography of the numeric score. `'compact'` drops from `b4` to `l1`. */
  scoreSize?: 'default' | 'compact';
  showRatingTooltip?: boolean;
  ratingTooltip?: string;
  tooltipSide?: TooltipProps['side'];
  /**
   * Star fill and chrome. `default` keeps warning-gold classes. `theme` follows
   * the tenant theme, and stays gold when that color is unset.
   * Default: `default`
   */
  starTone?: RatingStarTone;
  /**
   * When true, renders one star instead of five, filled when the score shown is
   * above 0 and empty otherwise.
   * Default: false
   */
  singleStar?: boolean;
  className?: string;
}

const MAX_STARS = 5;

function Star({
  fill,
  starTone = 'default',
}: {
  fill: number;
  starTone?: RatingStarTone;
}) {
  return (
    <span
      className='relative inline-flex shrink-0'
      data-star-fill={fill}
      aria-hidden
    >
      <Icon
        icon='star'
        size='sm'
        className={ratingStarStrokeVariants({ starTone })}
      />
      {fill > 0 && (
        <span
          className='absolute inset-y-0 left-0 flex items-center overflow-hidden'
          style={{ width: `${fill * 100}%` }}
        >
          <Icon
            icon='star'
            size='sm'
            className={ratingStarFillVariants({ starTone })}
          />
        </span>
      )}
    </span>
  );
}

function RatingInfoIcon({
  showRatingTooltip = false,
  ratingTooltip,
  tooltipSide,
}: Pick<
  PartnerStarRatingProps,
  'showRatingTooltip' | 'ratingTooltip' | 'tooltipSide'
>) {
  const tooltipContent = ratingTooltip?.trim();

  if (!showRatingTooltip || !tooltipContent) {
    return null;
  }

  return (
    <Tooltip content={tooltipContent} side={tooltipSide}>
      <Button
        type='button'
        variant='iconOnly'
        size='flush'
        iconBefore='info'
        className='shrink-0'
        aria-label='Info about partner rating'
      />
    </Tooltip>
  );
}

/**
 * Shared star-rating display for partner blocks. Not exported to the component catalog.
 *
 * By default renders five stars filled proportionally to the score, so a 4.5 shows four
 * full stars plus a half star beside the numeric value. `singleStar` renders one
 * star instead, filled whenever the score shown is above 0, and the number carries the
 * precision.
 */
export const PartnerStarRating: React.FC<PartnerStarRatingProps> = ({
  rating,
  ratingCount,
  showRatingCount = false,
  ratingCountAriaSuffix = '',
  scoreSize = 'default',
  showRatingTooltip = false,
  ratingTooltip,
  tooltipSide,
  starTone = 'default',
  singleStar = false,
  className,
}) => {
  const inRadioItemLabel = React.useContext(RadioItemLabelContext);
  const safeRating = Number.isFinite(rating) ? rating : 0;
  const displayRating = Math.min(Math.max(safeRating, 0), MAX_STARS);
  const formattedRating = displayRating.toFixed(1);
  const starFills = getStarFills(displayRating, singleStar);
  const groupAriaLabel = ratingCount
    ? `${formattedRating} out of ${MAX_STARS} stars, ${ratingCount}${ratingCountAriaSuffix}`
    : `${formattedRating} out of ${MAX_STARS} stars`;

  return (
    <Flex
      direction='row'
      align='center'
      gap='min'
      role='group'
      className={cn('shrink-0', className)}
      aria-label={groupAriaLabel}
      data-hen-id={COMPONENT_IDS.Rating}
    >
      {starFills.map((fill, index) => (
        <Star key={index} fill={fill} starTone={starTone} />
      ))}
      <Text
        variant={scoreSize === 'compact' ? 'l1' : 'b4'}
        weight='bold'
        tag='span'
      >
        {formattedRating}
      </Text>
      {showRatingCount && ratingCount ? (
        <Text variant='l2' color='subtext' tag='span'>
          {ratingCount}
        </Text>
      ) : null}
      <RatingInfoIcon
        showRatingTooltip={showRatingTooltip && !inRadioItemLabel}
        ratingTooltip={ratingTooltip}
        tooltipSide={tooltipSide}
      />
    </Flex>
  );
};

PartnerStarRating.displayName = 'PartnerStarRating';
